/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import * as tf from '@tensorflow/tfjs'
import TargetEncoder from './utils/TargetEncoder'
import MinMaxScaler from './utils/MinMaxScaler'
import RegressionModel from './utils/RegressionModel'
import {splitTrainValidation} from './utils/TrainValidationSplit'
import {TRAIN_VAL_RATIO} from './utils/Constants'


/**
 * Create & train Tensorflow regression model
 *
 * @param context {Object}: model config paramaters & data
 * @returns {Object} contains model and other metrics
 */
export function createRegressionModel(context) {
	if (!context.data || !context.types) {
		return
	}

	const y = context.data.y
	const encoder = new TargetEncoder(context.data.x, context.types, y)
	const x = encoder.encode(context.data.x)
	let xTensor = tf.tensor(x);

	const xNormalizer = new MinMaxScaler(xTensor)
	const yNormalizer = new MinMaxScaler(tf.tensor(y))

	let config = {
		xNormalizer,
		yNormalizer,
		encoder
	}
	config.batchSize = context.batchSize || false
	config.epochs = context.epochs || false
	config.layers = context.layers || false

	const { xNormTrain, yNormTrain, xNormVal, yNormVal, xNorm } = prepareTensors(x, y, xNormalizer, yNormalizer, xTensor)

	const regressionModel = new RegressionModel(config)
	return regressionModel.buildModelAndTrain(xNormTrain, yNormTrain, xNormVal, yNormVal).then(history => {
		// evaluate on the entire data (training and evaluation sets)
		let evl = evaluate(regressionModel, xNorm, yNormalizer, y)
		return {
			model: regressionModel,
			mse: evl.mse,
			r2: evl.r2,
			mae: evl.mae,
			history: history
		}
	})
}

/**
 * Train pretrained regression model. 
 * Note: validation train split is different and kept random. 
 * This is different than what used in createRegressionModel. 
 * In this way, the model is giving a chance to be trained over the entire dataset, 
 * including the validation set used in createRegressionModel(). 
 *
 * @param regressionModel {Object}: pre-trained model
 * @param data {Object}: data to re-train on 
 * @param config {Object}: OPTIONAL config to fit function 
 * @returns {Object} contains model and other metrics
 */
export function trainPretrainedModel(regressionModel, data, config) {
	if (!regressionModel) {
		throw new Error('model does not exist')
	}
	let batchSize
	let epochs
	let callbacks
	
	if (config) {
		batchSize = config.batchSize || regressionModel.batchSize
		epochs = config.epochs || regressionModel.epochs
		callbacks = config.callbacks || regressionModel.callbacks
	} else {
		batchSize = regressionModel.batchSize
		epochs = regressionModel.epochs
		callbacks = regressionModel.callbacks
	}

	const encoder = regressionModel.encoder;
	const x = encoder.encode(data.x)
	const xTensor = tf.tensor(x);
	const { xNormTrain, yNormTrain, xNormVal, yNormVal, xNorm } = prepareTensors(x, data.y, regressionModel.xNormalizer, regressionModel.yNormalizer, xTensor)
	
	const fitConfig = {
		batchSize: batchSize,
		epochs: epochs,
		validationData: [xNormVal, yNormVal],
		callbacks: callbacks
	}
	return regressionModel.model.fit(xNormTrain, yNormTrain, fitConfig).then(res => {
		// evaluate on the entire data (training and evaluation sets)
		const evl = evaluate(regressionModel, xNorm, regressionModel.yNormalizer, data.y)

		return {
			model: regressionModel,
			mse: evl.mse,
			r2: evl.r2,
			mae: evl.mae,
			history: res.history
		}
	})

}

function evaluate(model, xNorm, yNormalizer, y) {
	const yTrue = tf.tensor(y)
	const moments = tf.moments(yTrue)
	const variance = moments.variance.dataSync()[0]
	const yPredNorm = model.model.predict(xNorm)
	const yPred = yNormalizer.inverseTransform(yPredNorm).flatten()
	const mse = tf.metrics.meanSquaredError(yTrue, yPred).dataSync()[0];
	const mae = tf.metrics.meanAbsoluteError(yTrue, yPred).dataSync()[0];

	return {
		mae: mae,
		mse: mse,
		r2: 1 - (mse / variance)
	}
}

function prepareTensors(x, y, xNormalizer, yNormalizer, xTensor) {
	const { xTrain, yTrain, xVal, yVal } = splitTrainValidation(x, y, TRAIN_VAL_RATIO)
	const xTensorTrain = tf.tensor(xTrain)
	const yTensorTrain = tf.tensor(yTrain)
	const xTensorVal = tf.tensor(xVal)
	const yTensorVal = tf.tensor(yVal)
	const xNormTrain = xNormalizer.transform(xTensorTrain)
	const yNormTrain = yNormalizer.transform(yTensorTrain)
	const xNormVal = xNormalizer.transform(xTensorVal)
	const yNormVal = yNormalizer.transform(yTensorVal)
	// needed to evaluate on the entire data (training and evaluation sets)
	const xNorm = xNormalizer.transform(xTensor)
	return { xNormTrain, yNormTrain, xNormVal, yNormVal, xNorm }
}